import { Divider, Paper } from "@mui/material"
import { DeepReadonly } from "ts-essentials"
import { useCallback } from "react"
import { InstrumentMetadata } from "helpers/types/instruments/metadata"
import { SelectedInstrument } from "helpers/types/instruments/selected"
import BaseSelector from "connected-components/base-selector"
import InstrumentSelector from "connected-components/instrument-selector"
import { IsoDate } from "helpers/types/dates"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import ModelSelector from "../model-selector"
import DashboardTopBarCompare from "./compare"
import { ResourceMenuSwitcher } from "../resource-menu"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS, InstrumentSelectionData } from "../../analytics"
import { useCoreActions } from "../../hooks/use-core-actions"
import useOpenUnifiedSearch from "../../hooks/use-open-unified-search"

export default function DashboardTopBar({ instrumentMetadata, comparisonInstrumentMetadata, baseInstrumentMetadata, adjustedStartDate, onModelChange }: DeepReadonly<DashboardTopBarProps>) {
  const analytics = useAnalytics()
  const { setInstrument } = useCoreActions()

  const onBaseSelected = useCallback((base: SelectedInstrument | null, isDefault: boolean) => {
    if (base) {
      analytics.track(DASHBOARD_EVENTS.BASE_SELECTION, { base, isDefault })
    }
  }, [analytics])

  // The main instrument is per-instance, so its selector lives here (not in the global header).
  const onInstrumentSelection = useCallback(({ id, name, type, searchType, isInitialSelection }: InstrumentSelectionData) => {
    const payload = { instrument: { id, name, type }, searchType }
    analytics.track(isInitialSelection ? DASHBOARD_EVENTS.INITIAL_INSTRUMENT_SELECTION : DASHBOARD_EVENTS.COMPARISON_SEARCH_SELECTION, payload)
  }, [analytics])

  const openInstrumentSearch = useOpenUnifiedSearch(setInstrument, RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD, null, true, false, onInstrumentSelection)
  const onClearInstrument = useCallback(() => { setInstrument(null) }, [setInstrument])

  return <Paper square className="top-selectors">
    <div className="top-selectors-left">
      {instrumentMetadata && (
        <InstrumentSelector
          openSearch={openInstrumentSearch}
          selectedInstrumentMetadata={instrumentMetadata}
          onClear={onClearInstrument}
          size="small"
          // Portfolio actions (⋮ "More"), grouped inside the selector. Renders only for PORTFOLIO types.
          trailingActions={<ResourceMenuSwitcher />}
        />
      )}
      <Divider orientation="vertical" flexItem />
      <BaseSelector baseInstrumentMetadata={baseInstrumentMetadata} mainInstrumentMetadata={instrumentMetadata} onBaseSelected={onBaseSelected} />
      <Divider orientation="vertical" flexItem />
      <DashboardTopBarCompare comparisonInstrumentMetadata={comparisonInstrumentMetadata} adjustedStartDate={adjustedStartDate} />
    </div>
    <ModelSelector onModelChange={onModelChange} />
  </Paper>
}

interface DashboardTopBarProps {
  instrumentMetadata: InstrumentMetadata
  comparisonInstrumentMetadata?: InstrumentMetadata
  baseInstrumentMetadata?: InstrumentMetadata
  adjustedStartDate?: IsoDate
  onModelChange?: (modelId: string) => void
}
